import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolder,
  faFile,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileImage,
  faFileVideo,
  faFileAudio,
  faFileArchive,
  faEllipsisH,
  faGripVertical
} from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { bulkUpdateSortOrder } from '../../../../services/dropbox';
import { filenameIncludesExtension } from '../../utils/nameUtils';
import { toast } from 'react-toastify';

interface FileGridModernProps {
  folders: any[];
  files: any[];
  onFolderClick: (folderId: string) => void;
  onFileClick?: (file: any) => void;
  onContextMenu: (item: any, type: 'file' | 'folder', event: React.MouseEvent) => void;
  onRefresh?: () => void;
  contextMenuItemId?: string | null; // Add prop to track which item has context menu open
  currentView?: 'all' | 'favorites' | 'recent' | 'trash'; // Add currentView prop
}

interface SortableItemProps {
  id: string;
  item: any;
  type: 'file' | 'folder';
  isSelected: boolean;
  isHovered: boolean;
  onHover: (id: string | null) => void;
  onClick: (item: any, type: 'file' | 'folder', event: React.MouseEvent) => void;
  onDoubleClick: (item: any, type: 'file' | 'folder') => void;
  onContextMenu: (item: any, type: 'file' | 'folder', event: React.MouseEvent) => void;
  getFileIcon: (file: any) => { icon: any; className: string };
  contextMenuItemId?: string | null; // Add prop to track which item has context menu open
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  item,
  type,
  isSelected,
  isHovered,
  onHover,
  onClick,
  onDoubleClick,
  onContextMenu,
  getFileIcon,
  contextMenuItemId // Add the new prop
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const showControls = isHovered || isSelected;
  
  // Check if this item has the context menu open
  const hasContextMenu = contextMenuItemId === item.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dropbox-grid-item ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${hasContextMenu ? 'context-menu-active' : ''}`}
      onClick={(e) => onClick(item, type, e)}
      onDoubleClick={() => onDoubleClick(item, type)}
      onMouseEnter={() => onHover(id)}
      onMouseLeave={() => onHover(null)}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(item, type, e);
      }}
    >
      <div className="dropbox-grid-item-controls">
        <div className={`dropbox-grid-drag-handle ${showControls ? 'visible' : ''}`} {...attributes} {...listeners}>
          <FontAwesomeIcon icon={faGripVertical} />
        </div>
        <div className={`dropbox-grid-menu ${showControls ? 'visible' : ''}`}>
          <button
            className="dropbox-grid-menu-btn"
            onClick={(e) => {
              e.stopPropagation();
              onContextMenu(item, type, e);
            }}
          >
            <FontAwesomeIcon icon={faEllipsisH} />
          </button>
        </div>
      </div>
      {type === 'folder' ? (
        <div className="dropbox-file-icon dropbox-folder-icon">
          <FontAwesomeIcon icon={faFolder} />
        </div>
      ) : (
        <div className={`dropbox-file-icon ${getFileIcon(item).className}`}>
          <FontAwesomeIcon icon={getFileIcon(item).icon} />
        </div>
      )}
      <div className="dropbox-grid-item-name" title={item.name}>
        {item.name}
        {type === 'file' && item.extension && !filenameIncludesExtension(item.name, item.extension) && <span className="dropbox-file-extension">.{item.extension}</span>}
      </div>
    </div>
  );
};

const FileGridModern: React.FC<FileGridModernProps> = ({
  folders,
  files,
  onFolderClick,
  onFileClick,
  onContextMenu,
  onRefresh,
  contextMenuItemId, // Add the new prop
  currentView = 'all' // Add currentView with default value
}) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [items, setItems] = useState<Array<{ id: string; item: any; type: 'file' | 'folder' }>>([]);
  const { sortBy, sortDirection } = useSelector((state: any) => state.dropbox);

  // Combine folders and files into a single sortable array with Redux sorting
  // EXCEPT for Recent view, which maintains its own order based on accessed_at
  useMemo(() => {
    const combined = [
      ...folders.map(f => ({ id: `folder-${f.id}`, item: f, type: 'folder' as const })),
      ...files.map(f => ({ id: `file-${f.id}`, item: f, type: 'file' as const }))
    ];
    
    // For Recent view, preserve the order from getFilteredFiles (sorted by accessed_at)
    if (currentView !== 'recent') {
      // For other views, apply global sorting
      combined.sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
          case 'name':
            comparison = a.item.name.localeCompare(b.item.name);
            break;
          case 'date':
            const aDate = new Date(a.item.updated_at || 0).getTime();
            const bDate = new Date(b.item.updated_at || 0).getTime();
            comparison = aDate - bDate;
            break;
          case 'size':
            const aSize = a.type === 'file' ? (a.item.size_bytes || 0) : 0;
            const bSize = b.type === 'file' ? (b.item.size_bytes || 0) : 0;
            comparison = aSize - bSize;
            break;
          case 'custom':
            // Sort by sort_order field
            const orderA = a.item.sort_order ?? 0;
            const orderB = b.item.sort_order ?? 0;
            comparison = orderA - orderB;
            break;
          default:
            comparison = a.item.name.localeCompare(b.item.name);
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    
    setItems(combined);
  }, [folders, files, sortBy, sortDirection, currentView]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getFileIcon = (file: any) => {
    const ext = file.extension?.toLowerCase();
    const mime = file.mime_type?.toLowerCase();

    if (mime?.includes('pdf')) return { icon: faFilePdf, className: 'pdf' };
    if (mime?.includes('word') || ext === 'doc' || ext === 'docx') return { icon: faFileWord, className: 'word' };
    if (mime?.includes('excel') || ext === 'xls' || ext === 'xlsx') return { icon: faFileExcel, className: 'excel' };
    if (mime?.includes('powerpoint') || ext === 'ppt' || ext === 'pptx') return { icon: faFilePowerpoint, className: 'ppt' };
    if (mime?.includes('image')) return { icon: faFileImage, className: 'image' };
    if (mime?.includes('video')) return { icon: faFileVideo, className: 'video' };
    if (mime?.includes('audio')) return { icon: faFileAudio, className: 'audio' };
    if (mime?.includes('zip') || mime?.includes('rar') || mime?.includes('7z')) return { icon: faFileArchive, className: 'archive' };

    return { icon: faFile, className: 'default' };
  };

  const handleItemClick = (item: any, type: 'file' | 'folder', event: React.MouseEvent) => {
    const itemId = `${type}-${item.id}`;
    const newSelected = new Set(selectedItems);
    
    if (event.ctrlKey || event.metaKey) {
      if (newSelected.has(itemId)) {
        newSelected.delete(itemId);
      } else {
        newSelected.add(itemId);
      }
    } else {
      newSelected.clear();
      newSelected.add(itemId);
    }
    
    setSelectedItems(newSelected);
  };

  const handleItemDoubleClick = (item: any, type: 'file' | 'folder') => {
    if (type === 'folder') {
      onFolderClick(item.id);
    } else if (onFileClick) {
      onFileClick(item);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state immediately for smooth UX
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    // Prepare bulk update with new sort orders
    const updates = newItems.map((item, index) => ({
      id: item.item.id,
      type: item.type,
      sort_order: index
    }));

    try {
      const result = await bulkUpdateSortOrder(updates);
      
      if (!result?.success) {
        // Revert on error
        setItems(items);
        toast.error(result?.message || 'Failed to update order');
      }
      // Success: no toast, no refresh - just keep the optimistic update
    } catch (error) {
      // Revert on error
      setItems(items);
      toast.error('Failed to update order');
      console.error('Sort order update error:', error);
    }
  };

  const isSelected = (itemId: string) => selectedItems.has(itemId);

  const activeItem = activeId ? items.find(item => item.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map(item => item.id)} strategy={rectSortingStrategy}>
        <div className="dropbox-grid-modern">
          {items.map(({ id, item, type }) => (
            <SortableItem
              key={id}
              id={id}
              item={item}
              type={type}
              isSelected={isSelected(id)}
              isHovered={hoveredItem === id}
              onHover={setHoveredItem}
              onClick={handleItemClick}
              onDoubleClick={handleItemDoubleClick}
              onContextMenu={onContextMenu}
              getFileIcon={getFileIcon}
              contextMenuItemId={contextMenuItemId}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeItem ? (
          <div className="dropbox-grid-item dragging-overlay">
            {activeItem.type === 'folder' ? (
              <div className="dropbox-file-icon dropbox-folder-icon">
                <FontAwesomeIcon icon={faFolder} />
              </div>
            ) : (
              <div className={`dropbox-file-icon ${getFileIcon(activeItem.item).className}`}>
                <FontAwesomeIcon icon={getFileIcon(activeItem.item).icon} />
              </div>
            )}
            <div className="dropbox-grid-item-name">
              {activeItem.item.name}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default FileGridModern;
